import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  Building2,
  GraduationCap,
  Briefcase,
  Users,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const contactInfo = [
  {
    icon: Phone,
    title: 'Téléphone',
    content: '+689 87 73 78 15',
    link: 'tel:+68987737815'
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'contact@groupeaerhpolynesie.com',
    link: 'mailto:contact@groupeaerhpolynesie.com'
  },
  {
    icon: MapPin,
    title: 'Adresse',
    content: '10 Rue Georges Lagarde, Papeete',
    link: 'https://maps.google.com/?q=10+Rue+Georges+Lagarde+Papeete'
  },
  {
    icon: Clock,
    title: 'Horaires',
    content: 'Lun - Ven : 8h - 17h',
    link: null
  }
];

const subjects = [
  { value: 'formations', label: 'Formations', icon: GraduationCap },
  { value: 'consulting', label: 'Consulting', icon: Briefcase },
  { value: 'recrutement', label: 'Recrutement', icon: Users },
  { value: 'moana-paye', label: 'Moana Paye (Solutions Paie)', icon: Calculator },
  { value: 'location', label: 'Location de salles', icon: Building2 },
  { value: 'autre', label: 'Autre demande', icon: MessageSquare }
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succès ! Nous vous répondrons rapidement.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-teal-700" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="tapa" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect width="20" height="20" fill="none"/>
              <path d="M0 10 L10 0 L20 10 L10 20 Z" fill="currentColor" opacity="0.3"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#tapa)"/>
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <span className="inline-block bg-gold-500/20 text-gold-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Parlons de votre projet
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Notre équipe est à votre disposition pour répondre à vos questions 
              et vous accompagner dans vos projets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                {info.link ? (
                  <a href={info.link} className="block group" target={info.link.startsWith('http') ? '_blank' : undefined}>
                    <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mx-auto mb-3 group-hover:bg-gold-100 group-hover:text-gold-700 transition-colors">
                      <info.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                    <p className="text-slate-600 text-sm group-hover:text-teal-700 transition-colors">{info.content}</p>
                  </a>
                ) : (
                  <div>
                    <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mx-auto mb-3">
                      <info.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                    <p className="text-slate-600 text-sm">{info.content}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Envoyez-nous un message</h2>
                <p className="text-slate-600 mb-8">Remplissez le formulaire ci-dessous et nous vous répondrons dans les 48h.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Votre nom"
                        className="mt-1"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Entreprise / Structure</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder="Nom de votre structure"
                        className="mt-1"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="votre@email.com"
                        className="mt-1"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+689 87 XX XX XX"
                        className="mt-1"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Sujet de votre demande</Label>
                    <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                      <SelectTrigger className="mt-1" data-testid="contact-subject">
                        <SelectValue placeholder="Sélectionnez un sujet" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subject) => (
                          <SelectItem key={subject.value} value={subject.value}>
                            <div className="flex items-center gap-2">
                              <subject.icon className="w-4 h-4" />
                              {subject.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Votre message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Décrivez votre projet ou votre demande..."
                      className="mt-1 min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-6"
                    disabled={submitting}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Quick Contact */}
              <div className="bg-teal-700 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">Contact rapide</h3>
                <p className="text-teal-100 mb-6">
                  Besoin d'une réponse rapide ? Appelez-nous directement ou envoyez-nous un WhatsApp.
                </p>
                <div className="space-y-4">
                  <a 
                    href="tel:+68987737815"
                    className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-gold-400" />
                    <div>
                      <div className="text-sm text-teal-200">Téléphone</div>
                      <div className="font-medium">+689 87 73 78 15</div>
                    </div>
                  </a>
                  <a 
                    href="https://wa.me/68987737815?text=Bonjour"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 text-gold-400" />
                    <div>
                      <div className="text-sm text-teal-200">WhatsApp</div>
                      <div className="font-medium">Envoyer un message</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Services Quick Links */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Nos services</h3>
                <div className="space-y-3">
                  {subjects.filter(s => s.value !== 'autre').map((service) => {
                    const hrefMap = { formations: '/formations', consulting: '/consulting', recrutement: '/recrutement', 'moana-paye': '/moana-paye', location: '/location-salles' };
                    return (
                      <a
                        key={service.value}
                        href={hrefMap[service.value] || '/'}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center group-hover:bg-gold-100 group-hover:text-gold-700 transition-colors">
                          <service.icon className="w-5 h-5" />
                        </div>
                        <span className="font-medium text-slate-700 group-hover:text-teal-700 transition-colors">
                          {service.label}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Address */}
              <div className="bg-slate-100 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-teal-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Notre adresse</h4>
                    <p className="text-slate-600 text-sm">
                      10 Rue Georges Lagarde<br />
                      Papeete, Polynésie française
                    </p>
                    <a 
                      href="https://maps.google.com/?q=10+Rue+Georges+Lagarde+Papeete"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-700 text-sm font-medium mt-2 inline-block hover:text-gold-600"
                    >
                      Voir sur Google Maps →
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
